/**
 * mapper.js
 * Schema-driven helpers: page snapshot (for the AI backend), secret masking,
 * spec -> field mapping, and validation. Pages describe themselves with a
 * `fields` schema; nothing here knows about any specific page.
 *
 * Field schema:
 *  { key, label, type: "string"|"url"|"enum"|"json"|"number"|"boolean"|"keyvalue",
 *    required?, options?, sensitive?, aliases?, description?, when?(values)=>bool }
 */

export const MASK = "••••••";
const norm = (s) => String(s).toLowerCase().replace(/[^a-z0-9]/g, "");
const isActive = (f, values) => !f.when || f.when(values || {});
const isEmpty = (v) =>
  v == null || (typeof v === "string" && !v.trim()) || (typeof v === "object" && !Object.keys(v).length);

/* ---------- 1. Snapshot sent with every chat message ---------- */
export const redactValues = (fields, values = {}) => {
  const out = {};
  fields.forEach((f) => {
    const v = values[f.key];
    if (v === undefined) return;
    if (f.sensitive) {
      out[f.key] =
        f.type === "keyvalue" && v && typeof v === "object"
          ? Object.fromEntries(Object.keys(v).map((k) => [k, MASK]))
          : isEmpty(v) ? "" : MASK;
    } else out[f.key] = v;
  });
  return out;
};

export const buildPageSnapshot = (page, pathname) => ({
  route: pathname,
  page: { id: page.id, title: page.title, description: page.description },
  apis: page.apis || [],
  fields: page.fields.map(({ key, label, type, required, options, sensitive, description }) => ({
    key, label, type, required: !!required, options, sensitive: !!sensitive, description,
  })),
  currentValues: redactValues(page.fields, page.getValues()),
});

/* ---------- 2. Secret masking for the optional AI fallback ---------- */
const SECRET_PATTERNS = [
  /(?<=Bearer\s)[A-Za-z0-9._~+/=-]{8,}/g,
  /(?<=Basic\s)[A-Za-z0-9+/=]{8,}/g,
  /(?<=(?:api[_-]?key|apikey|token|secret|password|authkey|auth_key|access_token)["']?\s*[:=]\s*["']?)[^\s"'&,}]{6,}/gi,
];

export const maskSecrets = (text) => {
  const vault = [];
  let masked = text;
  SECRET_PATTERNS.forEach((re) => {
    masked = masked.replace(re, (m) => {
      vault.push(m);
      return `__SECRET_${vault.length - 1}__`;
    });
  });
  const restore = (val) =>
    typeof val === "string"
      ? val.replace(/__SECRET_(\d+)__/g, (_, i) => vault[+i] ?? "")
      : Array.isArray(val) ? val.map(restore)
      : val && typeof val === "object"
      ? Object.fromEntries(Object.entries(val).map(([k, v]) => [k, restore(v)]))
      : val;
  return { masked, restore };
};

/* ---------- 3. Spec -> values ---------- */
const flattenSpec = (spec) => {
  const flat = { method: spec.method, url: spec.baseUrl, baseurl: spec.baseUrl, contenttype: spec.contentType, body: spec.body };
  const walk = (o) =>
    Object.entries(o || {}).forEach(([k, v]) => {
      if (v && typeof v === "object") walk(v);
      else flat[norm(k)] = v;
    });
  walk(spec.query);
  walk(spec.headers);
  walk(spec.bodyJson);
  try {
    const host = new URL(spec.baseUrl).hostname.replace(/^(www|api)\./, "").split(".")[0];
    flat.provider = host.charAt(0).toUpperCase() + host.slice(1);
  } catch { /* ignore */ }
  return flat;
};

/** Generic mapping: field.key / field.aliases matched against spec keys. */
export const genericMap = (fields, spec) => {
  const flat = flattenSpec(spec);
  const values = {};
  fields.forEach((f) => {
    const hit = [f.key, ...(f.aliases || [])].map(norm).find((k) => flat[k] != null);
    if (hit) values[f.key] = flat[hit];
  });
  return values;
};

const coerce = (f, v) => {
  if (f.type === "enum") {
    const o = (f.options || []).find((x) => norm(x) === norm(v));
    return o ?? undefined;
  }
  if (f.type === "number") return Number.isFinite(+v) ? +v : undefined;
  if (f.type === "boolean") return v === true || v === "true" ? true : v === false || v === "false" ? false : undefined;
  if (f.type === "keyvalue") return v && typeof v === "object" && !Array.isArray(v) ? v : undefined;
  return v == null ? undefined : String(v);
};

/** Keep only schema keys with correctly typed values (also used on AI output). */
export const sanitizeValues = (fields, values = {}) => {
  const out = {};
  fields.forEach((f) => {
    if (!(f.key in values)) return;
    const c = coerce(f, values[f.key]);
    if (c !== undefined) out[f.key] = c;
  });
  return out;
};

/* ---------- 4. Validation ---------- */
export const validateValues = (fields, values = {}, custom) => {
  const errors = {};
  fields.forEach((f) => {
    if (!isActive(f, values)) return;
    const v = values[f.key];
    if (f.required && isEmpty(v)) return void (errors[f.key] = `${f.label} is required.`);
    if (isEmpty(v)) return;
    if (f.type === "url" && !/^https?:\/\/[^\s]+$/i.test(v)) errors[f.key] = "Must be a valid http(s) URL.";
    if (f.type === "enum" && !(f.options || []).includes(v)) errors[f.key] = `Must be one of: ${f.options.join(", ")}.`;
    if (f.type === "json") {
      try { JSON.parse(String(v).replace(/"?\{\{\s*\w+\s*\}\}"?/g, '"x"')); }
      catch { errors[f.key] = "Must be valid JSON."; }
    }
  });
  Object.assign(errors, custom?.(values) || {});
  return errors;
};

/* ---------- 5. Draft ---------- */
export const buildDraft = (page, spec) => {
  const raw = page.mapSpec ? page.mapSpec(spec) : genericMap(page.fields, spec);
  const values = sanitizeValues(page.fields, raw);
  return { id: `d_${Date.now()}`, pageId: page.id, spec, values, applied: false, saved: false };
};

export const activeFields = (fields, values) => fields.filter((f) => isActive(f, values));
